package com.starparty.app.vector_maths

import kotlin.math.abs
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.sqrt

class Matrix3d {
    var m00: Double
    var m01: Double
    var m02: Double
    var m10: Double
    var m11: Double
    var m12: Double
    var m20: Double
    var m21: Double
    var m22: Double

    constructor(
        m00: Double,
        m01: Double,
        m02: Double,
        m10: Double,
        m11: Double,
        m12: Double,
        m20: Double,
        m21: Double,
        m22: Double
    ) {
        this.m00 = m00
        this.m01 = m01
        this.m02 = m02
        this.m10 = m10
        this.m11 = m11
        this.m12 = m12
        this.m20 = m20
        this.m21 = m21
        this.m22 = m22
    }

    constructor(v: DoubleArray) {
        m00 = v[0]
        m01 = v[1]
        m02 = v[2]
        m10 = v[3]
        m11 = v[4]
        m12 = v[5]
        m20 = v[6]
        m21 = v[7]
        m22 = v[8]
    }

    constructor(m1: Matrix3d) {
        m00 = m1.m00
        m01 = m1.m01
        m02 = m1.m02
        m10 = m1.m10
        m11 = m1.m11
        m12 = m1.m12
        m20 = m1.m20
        m21 = m1.m21
        m22 = m1.m22
    }

    constructor() {
        m00 = 0.0
        m01 = 0.0
        m02 = 0.0
        m10 = 0.0
        m11 = 0.0
        m12 = 0.0
        m20 = 0.0
        m21 = 0.0
        m22 = 0.0
    }

    override fun toString(): String {
        return """
            ${m00}, ${m01}, ${m02}
            ${m10}, ${m11}, ${m12}
            ${m20}, ${m21}, ${m22}
            
            """.trimIndent()
    }

    fun setIdentity() {
        m00 = 1.0
        m01 = 0.0
        m02 = 0.0
        m10 = 0.0
        m11 = 1.0
        m12 = 0.0
        m20 = 0.0
        m21 = 0.0
        m22 = 1.0
    }

    fun setElement(row: Int, column: Int, value: Double) {
        when (row) {
            0 -> when (column) {
                0 -> {
                    m00 = value
                    return
                }
                1 -> {
                    m01 = value
                    return
                }
                2 -> {
                    m02 = value
                    return
                }
                else -> throw IndexOutOfBoundsException()
            }
            1 -> when (column) {
                0 -> {
                    m10 = value
                    return
                }
                1 -> {
                    m11 = value
                    return
                }
                2 -> {
                    m12 = value
                    return
                }
                else -> throw IndexOutOfBoundsException()
            }
            2 -> when (column) {
                0 -> {
                    m20 = value
                    return
                }
                1 -> {
                    m21 = value
                    return
                }
                2 -> {
                    m22 = value
                    return
                }
                else -> throw IndexOutOfBoundsException()
            }
            else -> throw IndexOutOfBoundsException()
        }
    }


    fun getElement(row: Int, column: Int): Double {
        when (row) {
            0 -> return when (column) {
                0 -> m00
                1 -> m01
                2 -> m02
                else -> throw IndexOutOfBoundsException()
            }
            1 -> return when (column) {
                0 -> m10
                1 -> m11
                2 -> m12
                else -> throw IndexOutOfBoundsException()
            }
            2 -> when (column) {
                0 -> return m20
                1 -> return m21
                2 -> return m22
            }
        }
        throw IndexOutOfBoundsException()
    }


    fun getRow(row: Int, v: Vector3d) {
        if (row == 0) {
            v.x = m00
            v.y = m01
            v.z = m02
        } else if (row == 1) {
            v.x = m10
            v.y = m11
            v.z = m12
        } else {
            if (row != 2) {
                throw IndexOutOfBoundsException()
            }
            v.x = m20
            v.y = m21
            v.z = m22
        }
    }


    fun getRow(row: Int, v: DoubleArray) {
        if (row == 0) {
            v[0] = m00
            v[1] = m01
            v[2] = m02
        } else if (row == 1) {
            v[0] = m10
            v[1] = m11
            v[2] = m12
        } else {
            if (row != 2) {
                throw IndexOutOfBoundsException()
            }
            v[0] = m20
            v[1] = m21
            v[2] = m22
        }
    }

    fun getColumn(column: Int, v: Vector3d) {
        if (column == 0) {
            v.x = m00
            v.y = m10
            v.z = m20
        } else if (column == 1) {
            v.x = m01
            v.y = m11
            v.z = m21
        } else {
            if (column != 2) {
                throw IndexOutOfBoundsException()
            }
            v.x = m02
            v.y = m12
            v.z = m22
        }
    }

    fun getColumn(column: Int, v: DoubleArray) {
        if (column == 0) {
            v[0] = m00
            v[1] = m10
            v[2] = m20
        } else if (column == 1) {
            v[0] = m01
            v[1] = m11
            v[2] = m21
        } else {
            if (column != 2) {
                throw IndexOutOfBoundsException()
            }
            v[0] = m02
            v[1] = m12
            v[2] = m22
        }
    }

    fun setRow(row: Int, x: Double, y: Double, z: Double) {
        when (row) {
            0 -> {
                m00 = x
                m01 = y
                m02 = z
            }
            1 -> {
                m10 = x
                m11 = y
                m12 = z
            }
            2 -> {
                m20 = x
                m21 = y
                m22 = z
            }
            else -> throw IndexOutOfBoundsException()
        }
    }

    fun setRow(row: Int, v: Vector3d) {
        when (row) {
            0 -> {
                m00 = v.x
                m01 = v.y
                m02 = v.z
            }
            1 -> {
                m10 = v.x
                m11 = v.y
                m12 = v.z
            }
            2 -> {
                m20 = v.x
                m21 = v.y
                m22 = v.z
            }
            else -> throw IndexOutOfBoundsException()
        }
    }

    fun setRow(row: Int, v: DoubleArray) {
        when (row) {
            0 -> {
                m00 = v[0]
                m01 = v[1]
                m02 = v[2]
            }
            1 -> {
                m10 = v[0]
                m11 = v[1]
                m12 = v[2]
            }
            2 -> {
                m20 = v[0]
                m21 = v[1]
                m22 = v[2]
            }
            else -> throw IndexOutOfBoundsException()
        }
    }

    fun setColumn(column: Int, x: Double, y: Double, z: Double) {
        when (column) {
            0 -> {
                m00 = x
                m10 = y
                m20 = z
            }
            1 -> {
                m01 = x
                m11 = y
                m21 = z
            }
            2 -> {
                m02 = x
                m12 = y
                m22 = z
            }
            else -> throw IndexOutOfBoundsException()
        }
    }

    fun setColumn(column: Int, v: Vector3d) {
        when (column) {
            0 -> {
                m00 = v.x
                m10 = v.y
                m20 = v.z
            }
            1 -> {
                m01 = v.x
                m11 = v.y
                m21 = v.z
            }
            2 -> {
                m02 = v.x
                m12 = v.y
                m22 = v.z
            }
            else -> throw IndexOutOfBoundsException()
        }
    }

    fun setColumn(column: Int, v: DoubleArray) {
        when (column) {
            0 -> {
                m00 = v[0]
                m10 = v[1]
                m20 = v[2]
            }
            1 -> {
                m01 = v[0]
                m11 = v[1]
                m21 = v[2]
            }
            2 -> {
                m02 = v[0]
                m12 = v[1]
                m22 = v[2]
            }
            else -> throw IndexOutOfBoundsException()
        }
    }

    var scale: Double
        get() {
            val tmp_scale = DoubleArray(3)
            val tmp_rot = DoubleArray(9)
            getScaleRotate(tmp_scale, tmp_rot)
            return max3(tmp_scale)
        }
        set(scale) {
            val tmp_rot = DoubleArray(9)
            val tmp_scale = DoubleArray(3)
            getScaleRotate(tmp_scale, tmp_rot)
            m00 = tmp_rot[0] * scale
            m01 = tmp_rot[1] * scale
            m02 = tmp_rot[2] * scale
            m10 = tmp_rot[3] * scale
            m11 = tmp_rot[4] * scale
            m12 = tmp_rot[5] * scale
            m20 = tmp_rot[6] * scale
            m21 = tmp_rot[7] * scale
            m22 = tmp_rot[8] * scale
        }

    fun add(scalar: Double) {
        m00 += scalar
        m01 += scalar
        m02 += scalar
        m10 += scalar
        m11 += scalar
        m12 += scalar
        m20 += scalar
        m21 += scalar
        m22 += scalar
    }

    fun add(scalar: Double, m1: Matrix3d) {
        m00 = m1.m00 + scalar
        m01 = m1.m01 + scalar
        m02 = m1.m02 + scalar
        m10 = m1.m10 + scalar
        m11 = m1.m11 + scalar
        m12 = m1.m12 + scalar
        m20 = m1.m20 + scalar
        m21 = m1.m21 + scalar
        m22 = m1.m22 + scalar
    }

    fun add(m1: Matrix3d, m2: Matrix3d) {
        m00 = m1.m00 + m2.m00
        m01 = m1.m01 + m2.m01
        m02 = m1.m02 + m2.m02
        m10 = m1.m10 + m2.m10
        m11 = m1.m11 + m2.m11
        m12 = m1.m12 + m2.m12
        m20 = m1.m20 + m2.m20
        m21 = m1.m21 + m2.m21
        m22 = m1.m22 + m2.m22
    }

    fun add(m1: Matrix3d) {
        m00 += m1.m00
        m01 += m1.m01
        m02 += m1.m02
        m10 += m1.m10
        m11 += m1.m11
        m12 += m1.m12
        m20 += m1.m20
        m21 += m1.m21
        m22 += m1.m22
    }

    fun sub(m1: Matrix3d, m2: Matrix3d) {
        m00 = m1.m00 - m2.m00
        m01 = m1.m01 - m2.m01
        m02 = m1.m02 - m2.m02
        m10 = m1.m10 - m2.m10
        m11 = m1.m11 - m2.m11
        m12 = m1.m12 - m2.m12
        m20 = m1.m20 - m2.m20
        m21 = m1.m21 - m2.m21
        m22 = m1.m22 - m2.m22
    }

    fun sub(m1: Matrix3d) {
        m00 -= m1.m00
        m01 -= m1.m01
        m02 -= m1.m02
        m10 -= m1.m10
        m11 -= m1.m11
        m12 -= m1.m12
        m20 -= m1.m20
        m21 -= m1.m21
        m22 -= m1.m22
    }

    fun transpose() {
        var temp = m10
        m10 = m01
        m01 = temp
        temp = m20
        m20 = m02
        m02 = temp
        temp = m21
        m21 = m12
        m12 = temp
    }

    fun transpose(m1: Matrix3d) {
        if (this !== m1) {
            m00 = m1.m00
            m01 = m1.m10
            m02 = m1.m20
            m10 = m1.m01
            m11 = m1.m11
            m12 = m1.m21
            m20 = m1.m02
            m21 = m1.m12
            m22 = m1.m22
        } else {
            this.transpose()
        }
    }

    fun set(m1: Matrix3d) {
        m00 = m1.m00
        m01 = m1.m01
        m02 = m1.m02
        m10 = m1.m10
        m11 = m1.m11
        m12 = m1.m12
        m20 = m1.m20
        m21 = m1.m21
        m22 = m1.m22
    }

    fun set(m: DoubleArray) {
        m00 = m[0]
        m01 = m[1]
        m02 = m[2]
        m10 = m[3]
        m11 = m[4]
        m12 = m[5]
        m20 = m[6]
        m21 = m[7]
        m22 = m[8]
    }

    fun invert(m1: Matrix3d) {
        invertGeneral(m1)
    }

    fun invert() {
        invertGeneral(this)
    }

    private fun invertGeneral(m1: Matrix3d) {
        val result = DoubleArray(9)
        val row_perm = IntArray(3)
        val tmp =
            doubleArrayOf(m1.m00, m1.m01, m1.m02, m1.m10, m1.m11, m1.m12, m1.m20, m1.m21, m1.m22)
        if (!luDecomposition(tmp, row_perm)) {
            throw RuntimeException("SingularMatrixException")
        } else {
            for (i in 0..8) {
                result[i] = 0.0
            }
            result[0] = 1.0
            result[4] = 1.0
            result[8] = 1.0
            luBacksubstitution(tmp, row_perm, result)
            m00 = result[0]
            m01 = result[1]
            m02 = result[2]
            m10 = result[3]
            m11 = result[4]
            m12 = result[5]
            m20 = result[6]
            m21 = result[7]
            m22 = result[8]
        }
    }

    fun determinant(): Double {
        return m00 * (m11 * m22 - m12 * m21) + m01 * (m12 * m20 - m10 * m22) + m02 * (m10 * m21 - m11 * m20)
    }

    fun set(scale: Double) {
        m00 = scale
        m01 = 0.0
        m02 = 0.0
        m10 = 0.0
        m11 = scale
        m12 = 0.0
        m20 = 0.0
        m21 = 0.0
        m22 = scale
    }

    fun rotX(angle: Double) {
        val sinAngle = sin(angle)
        val cosAngle = cos(angle)
        m00 = 1.0
        m01 = 0.0
        m02 = 0.0
        m10 = 0.0
        m11 = cosAngle
        m12 = -sinAngle
        m20 = 0.0
        m21 = sinAngle
        m22 = cosAngle
    }

    fun rotY(angle: Double) {
        val sinAngle = sin(angle)
        val cosAngle = cos(angle)
        m00 = cosAngle
        m01 = 0.0
        m02 = sinAngle
        m10 = 0.0
        m11 = 1.0
        m12 = 0.0
        m20 = -sinAngle
        m21 = 0.0
        m22 = cosAngle
    }

    fun rotZ(angle: Double) {
        val sinAngle = sin(angle)
        val cosAngle = cos(angle)
        m00 = cosAngle
        m01 = -sinAngle
        m02 = 0.0
        m10 = sinAngle
        m11 = cosAngle
        m12 = 0.0
        m20 = 0.0
        m21 = 0.0
        m22 = 1.0
    }

    fun mul(scalar: Double) {
        m00 *= scalar
        m01 *= scalar
        m02 *= scalar
        m10 *= scalar
        m11 *= scalar
        m12 *= scalar
        m20 *= scalar
        m21 *= scalar
        m22 *= scalar
    }

    fun mul(scalar: Double, m1: Matrix3d) {
        m00 = scalar * m1.m00
        m01 = scalar * m1.m01
        m02 = scalar * m1.m02
        m10 = scalar * m1.m10
        m11 = scalar * m1.m11
        m12 = scalar * m1.m12
        m20 = scalar * m1.m20
        m21 = scalar * m1.m21
        m22 = scalar * m1.m22
    }

    fun mul(m1: Matrix3d) {
        val m00 = m00 * m1.m00 + m01 * m1.m10 + m02 * m1.m20
        val m01 = this.m00 * m1.m01 + m01 * m1.m11 + m02 * m1.m21
        val m02 = this.m00 * m1.m02 + this.m01 * m1.m12 + m02 * m1.m22
        val m10 = m10 * m1.m00 + m11 * m1.m10 + m12 * m1.m20
        val m11 = this.m10 * m1.m01 + m11 * m1.m11 + m12 * m1.m21
        val m12 = this.m10 * m1.m02 + this.m11 * m1.m12 + m12 * m1.m22
        val m20 = m20 * m1.m00 + m21 * m1.m10 + m22 * m1.m20
        val m21 = this.m20 * m1.m01 + m21 * m1.m11 + m22 * m1.m21
        val m22 = this.m20 * m1.m02 + this.m21 * m1.m12 + m22 * m1.m22
        this.m00 = m00
        this.m01 = m01
        this.m02 = m02
        this.m10 = m10
        this.m11 = m11
        this.m12 = m12
        this.m20 = m20
        this.m21 = m21
        this.m22 = m22
    }

    fun mul(m1: Matrix3d, m2: Matrix3d) {
        if (this !== m1 && this !== m2) {
            m00 = m1.m00 * m2.m00 + m1.m01 * m2.m10 + m1.m02 * m2.m20
            m01 = m1.m00 * m2.m01 + m1.m01 * m2.m11 + m1.m02 * m2.m21
            m02 = m1.m00 * m2.m02 + m1.m01 * m2.m12 + m1.m02 * m2.m22
            m10 = m1.m10 * m2.m00 + m1.m11 * m2.m10 + m1.m12 * m2.m20
            m11 = m1.m10 * m2.m01 + m1.m11 * m2.m11 + m1.m12 * m2.m21
            m12 = m1.m10 * m2.m02 + m1.m11 * m2.m12 + m1.m12 * m2.m22
            m20 = m1.m20 * m2.m00 + m1.m21 * m2.m10 + m1.m22 * m2.m20
            m21 = m1.m20 * m2.m01 + m1.m21 * m2.m11 + m1.m22 * m2.m21
            m22 = m1.m20 * m2.m02 + m1.m21 * m2.m12 + m1.m22 * m2.m22
        } else {
            val m00 = m1.m00 * m2.m00 + m1.m01 * m2.m10 + m1.m02 * m2.m20
            val m01 = m1.m00 * m2.m01 + m1.m01 * m2.m11 + m1.m02 * m2.m21
            val m02 = m1.m00 * m2.m02 + m1.m01 * m2.m12 + m1.m02 * m2.m22
            val m10 = m1.m10 * m2.m00 + m1.m11 * m2.m10 + m1.m12 * m2.m20
            val m11 = m1.m10 * m2.m01 + m1.m11 * m2.m11 + m1.m12 * m2.m21
            val m12 = m1.m10 * m2.m02 + m1.m11 * m2.m12 + m1.m12 * m2.m22
            val m20 = m1.m20 * m2.m00 + m1.m21 * m2.m10 + m1.m22 * m2.m20
            val m21 = m1.m20 * m2.m01 + m1.m21 * m2.m11 + m1.m22 * m2.m21
            val m22 = m1.m20 * m2.m02 + m1.m21 * m2.m12 + m1.m22 * m2.m22
            this.m00 = m00
            this.m01 = m01
            this.m02 = m02
            this.m10 = m10
            this.m11 = m11
            this.m12 = m12
            this.m20 = m20
            this.m21 = m21
            this.m22 = m22
        }
    }

    fun mulNormalize(m1: Matrix3d) {
        val tmp = DoubleArray(9)
        val tmp_rot = DoubleArray(9)
        val tmp_scale = DoubleArray(3)
        tmp[0] = m00 * m1.m00 + m01 * m1.m10 + m02 * m1.m20
        tmp[1] = m00 * m1.m01 + m01 * m1.m11 + m02 * m1.m21
        tmp[2] = m00 * m1.m02 + m01 * m1.m12 + m02 * m1.m22
        tmp[3] = m10 * m1.m00 + m11 * m1.m10 + m12 * m1.m20
        tmp[4] = m10 * m1.m01 + m11 * m1.m11 + m12 * m1.m21
        tmp[5] = m10 * m1.m02 + m11 * m1.m12 + m12 * m1.m22
        tmp[6] = m20 * m1.m00 + m21 * m1.m10 + m22 * m1.m20
        tmp[7] = m20 * m1.m01 + m21 * m1.m11 + m22 * m1.m21
        tmp[8] = m20 * m1.m02 + m21 * m1.m12 + m22 * m1.m22
        compute_svd(tmp, tmp_scale, tmp_rot)
        m00 = tmp_rot[0]
        m01 = tmp_rot[1]
        m02 = tmp_rot[2]
        m10 = tmp_rot[3]
        m11 = tmp_rot[4]
        m12 = tmp_rot[5]
        m20 = tmp_rot[6]
        m21 = tmp_rot[7]
        m22 = tmp_rot[8]
    }

    fun mulNormalize(m1: Matrix3d, m2: Matrix3d) {
        val tmp = DoubleArray(9)
        val tmp_rot = DoubleArray(9)
        val tmp_scale = DoubleArray(3)
        tmp[0] = m1.m00 * m2.m00 + m1.m01 * m2.m10 + m1.m02 * m2.m20
        tmp[1] = m1.m00 * m2.m01 + m1.m01 * m2.m11 + m1.m02 * m2.m21
        tmp[2] = m1.m00 * m2.m02 + m1.m01 * m2.m12 + m1.m02 * m2.m22
        tmp[3] = m1.m10 * m2.m00 + m1.m11 * m2.m10 + m1.m12 * m2.m20
        tmp[4] = m1.m10 * m2.m01 + m1.m11 * m2.m11 + m1.m12 * m2.m21
        tmp[5] = m1.m10 * m2.m02 + m1.m11 * m2.m12 + m1.m12 * m2.m22
        tmp[6] = m1.m20 * m2.m00 + m1.m21 * m2.m10 + m1.m22 * m2.m20
        tmp[7] = m1.m20 * m2.m01 + m1.m21 * m2.m11 + m1.m22 * m2.m21
        tmp[8] = m1.m20 * m2.m02 + m1.m21 * m2.m12 + m1.m22 * m2.m22
        compute_svd(tmp, tmp_scale, tmp_rot)
        m00 = tmp_rot[0]
        m01 = tmp_rot[1]
        m02 = tmp_rot[2]
        m10 = tmp_rot[3]
        m11 = tmp_rot[4]
        m12 = tmp_rot[5]
        m20 = tmp_rot[6]
        m21 = tmp_rot[7]
        m22 = tmp_rot[8]
    }

    fun mulTransposeBoth(m1: Matrix3d, m2: Matrix3d) {
        if (this !== m1 && this !== m2) {
            m00 = m1.m00 * m2.m00 + m1.m10 * m2.m01 + m1.m20 * m2.m02
            m01 = m1.m00 * m2.m10 + m1.m10 * m2.m11 + m1.m20 * m2.m12
            m02 = m1.m00 * m2.m20 + m1.m10 * m2.m21 + m1.m20 * m2.m22
            m10 = m1.m01 * m2.m00 + m1.m11 * m2.m01 + m1.m21 * m2.m02
            m11 = m1.m01 * m2.m10 + m1.m11 * m2.m11 + m1.m21 * m2.m12
            m12 = m1.m01 * m2.m20 + m1.m11 * m2.m21 + m1.m21 * m2.m22
            m20 = m1.m02 * m2.m00 + m1.m12 * m2.m01 + m1.m22 * m2.m02
            m21 = m1.m02 * m2.m10 + m1.m12 * m2.m11 + m1.m22 * m2.m12
            m22 = m1.m02 * m2.m20 + m1.m12 * m2.m21 + m1.m22 * m2.m22
        } else {
            val m00 = m1.m00 * m2.m00 + m1.m10 * m2.m01 + m1.m20 * m2.m02
            val m01 = m1.m00 * m2.m10 + m1.m10 * m2.m11 + m1.m20 * m2.m12
            val m02 = m1.m00 * m2.m20 + m1.m10 * m2.m21 + m1.m20 * m2.m22
            val m10 = m1.m01 * m2.m00 + m1.m11 * m2.m01 + m1.m21 * m2.m02
            val m11 = m1.m01 * m2.m10 + m1.m11 * m2.m11 + m1.m21 * m2.m12
            val m12 = m1.m01 * m2.m20 + m1.m11 * m2.m21 + m1.m21 * m2.m22
            val m20 = m1.m02 * m2.m00 + m1.m12 * m2.m01 + m1.m22 * m2.m02
            val m21 = m1.m02 * m2.m10 + m1.m12 * m2.m11 + m1.m22 * m2.m12
            val m22 = m1.m02 * m2.m20 + m1.m12 * m2.m21 + m1.m22 * m2.m22
            this.m00 = m00
            this.m01 = m01
            this.m02 = m02
            this.m10 = m10
            this.m11 = m11
            this.m12 = m12
            this.m20 = m20
            this.m21 = m21
            this.m22 = m22
        }
    }

    fun mulTransposeRight(m1: Matrix3d, m2: Matrix3d) {
        if (this !== m1 && this !== m2) {
            m00 = m1.m00 * m2.m00 + m1.m01 * m2.m01 + m1.m02 * m2.m02
            m01 = m1.m00 * m2.m10 + m1.m01 * m2.m11 + m1.m02 * m2.m12
            m02 = m1.m00 * m2.m20 + m1.m01 * m2.m21 + m1.m02 * m2.m22
            m10 = m1.m10 * m2.m00 + m1.m11 * m2.m01 + m1.m12 * m2.m02
            m11 = m1.m10 * m2.m10 + m1.m11 * m2.m11 + m1.m12 * m2.m12
            m12 = m1.m10 * m2.m20 + m1.m11 * m2.m21 + m1.m12 * m2.m22
            m20 = m1.m20 * m2.m00 + m1.m21 * m2.m01 + m1.m22 * m2.m02
            m21 = m1.m20 * m2.m10 + m1.m21 * m2.m11 + m1.m22 * m2.m12
            m22 = m1.m20 * m2.m20 + m1.m21 * m2.m21 + m1.m22 * m2.m22
        } else {
            val m00 = m1.m00 * m2.m00 + m1.m01 * m2.m01 + m1.m02 * m2.m02
            val m01 = m1.m00 * m2.m10 + m1.m01 * m2.m11 + m1.m02 * m2.m12
            val m02 = m1.m00 * m2.m20 + m1.m01 * m2.m21 + m1.m02 * m2.m22
            val m10 = m1.m10 * m2.m00 + m1.m11 * m2.m01 + m1.m12 * m2.m02
            val m11 = m1.m10 * m2.m10 + m1.m11 * m2.m11 + m1.m12 * m2.m12
            val m12 = m1.m10 * m2.m20 + m1.m11 * m2.m21 + m1.m12 * m2.m22
            val m20 = m1.m20 * m2.m00 + m1.m21 * m2.m01 + m1.m22 * m2.m02
            val m21 = m1.m20 * m2.m10 + m1.m21 * m2.m11 + m1.m22 * m2.m12
            val m22 = m1.m20 * m2.m20 + m1.m21 * m2.m21 + m1.m22 * m2.m22
            this.m00 = m00
            this.m01 = m01
            this.m02 = m02
            this.m10 = m10
            this.m11 = m11
            this.m12 = m12
            this.m20 = m20
            this.m21 = m21
            this.m22 = m22
        }
    }

    fun mulTransposeLeft(m1: Matrix3d, m2: Matrix3d) {
        if (this !== m1 && this !== m2) {
            m00 = m1.m00 * m2.m00 + m1.m10 * m2.m10 + m1.m20 * m2.m20
            m01 = m1.m00 * m2.m01 + m1.m10 * m2.m11 + m1.m20 * m2.m21
            m02 = m1.m00 * m2.m02 + m1.m10 * m2.m12 + m1.m20 * m2.m22
            m10 = m1.m01 * m2.m00 + m1.m11 * m2.m10 + m1.m21 * m2.m20
            m11 = m1.m01 * m2.m01 + m1.m11 * m2.m11 + m1.m21 * m2.m21
            m12 = m1.m01 * m2.m02 + m1.m11 * m2.m12 + m1.m21 * m2.m22
            m20 = m1.m02 * m2.m00 + m1.m12 * m2.m10 + m1.m22 * m2.m20
            m21 = m1.m02 * m2.m01 + m1.m12 * m2.m11 + m1.m22 * m2.m21
            m22 = m1.m02 * m2.m02 + m1.m12 * m2.m12 + m1.m22 * m2.m22
        } else {
            val m00 = m1.m00 * m2.m00 + m1.m10 * m2.m10 + m1.m20 * m2.m20
            val m01 = m1.m00 * m2.m01 + m1.m10 * m2.m11 + m1.m20 * m2.m21
            val m02 = m1.m00 * m2.m02 + m1.m10 * m2.m12 + m1.m20 * m2.m22
            val m10 = m1.m01 * m2.m00 + m1.m11 * m2.m10 + m1.m21 * m2.m20
            val m11 = m1.m01 * m2.m01 + m1.m11 * m2.m11 + m1.m21 * m2.m21
            val m12 = m1.m01 * m2.m02 + m1.m11 * m2.m12 + m1.m21 * m2.m22
            val m20 = m1.m02 * m2.m00 + m1.m12 * m2.m10 + m1.m22 * m2.m20
            val m21 = m1.m02 * m2.m01 + m1.m12 * m2.m11 + m1.m22 * m2.m21
            val m22 = m1.m02 * m2.m02 + m1.m12 * m2.m12 + m1.m22 * m2.m22
            this.m00 = m00
            this.m01 = m01
            this.m02 = m02
            this.m10 = m10
            this.m11 = m11
            this.m12 = m12
            this.m20 = m20
            this.m21 = m21
            this.m22 = m22
        }
    }

    fun normalize() {
        val tmp_rot = DoubleArray(9)
        val tmp_scale = DoubleArray(3)
        getScaleRotate(tmp_scale, tmp_rot)
        m00 = tmp_rot[0]
        m01 = tmp_rot[1]
        m02 = tmp_rot[2]
        m10 = tmp_rot[3]
        m11 = tmp_rot[4]
        m12 = tmp_rot[5]
        m20 = tmp_rot[6]
        m21 = tmp_rot[7]
        m22 = tmp_rot[8]
    }

    fun normalize(m1: Matrix3d) {
        val tmp = DoubleArray(9)
        val tmp_rot = DoubleArray(9)
        val tmp_scale = DoubleArray(3)
        tmp[0] = m1.m00
        tmp[1] = m1.m01
        tmp[2] = m1.m02
        tmp[3] = m1.m10
        tmp[4] = m1.m11
        tmp[5] = m1.m12
        tmp[6] = m1.m20
        tmp[7] = m1.m21
        tmp[8] = m1.m22
        compute_svd(tmp, tmp_scale, tmp_rot)
        m00 = tmp_rot[0]
        m01 = tmp_rot[1]
        m02 = tmp_rot[2]
        m10 = tmp_rot[3]
        m11 = tmp_rot[4]
        m12 = tmp_rot[5]
        m20 = tmp_rot[6]
        m21 = tmp_rot[7]
        m22 = tmp_rot[8]
    }

    fun normalizeCP() {
        var mag =
            1.0 / sqrt(m00 * m00 + m10 * m10 + m20 * m20)
        m00 *= mag
        m10 *= mag
        m20 *= mag
        mag =
            1.0 / sqrt(m01 * m01 + m11 * m11 + m21 * m21)
        m01 *= mag
        m11 *= mag
        m21 *= mag
        m02 = m10 * m21 - m11 * m20
        m12 = m01 * m20 - m00 * m21
        m22 = m00 * m11 - m01 * m10
    }

    fun normalizeCP(m1: Matrix3d) {
        var mag =
            1.0 / sqrt(m1.m00 * m1.m00 + m1.m10 * m1.m10 + m1.m20 * m1.m20)
        m00 = m1.m00 * mag
        m10 = m1.m10 * mag
        m20 = m1.m20 * mag
        mag = 1.0 / sqrt(m1.m01 * m1.m01 + m1.m11 * m1.m11 + m1.m21 * m1.m21)
        m01 = m1.m01 * mag
        m11 = m1.m11 * mag
        m21 = m1.m21 * mag
        m02 = m10 * m21 - m11 * m20
        m12 = m01 * m20 - m00 * m21
        m22 = m00 * m11 - m01 * m10
    }

    fun equals(m1: Matrix3d): Boolean {
        return try {
            m00 == m1.m00 && m01 == m1.m01 && m02 == m1.m02 && m10 == m1.m10 && m11 == m1.m11 && m12 == m1.m12 && m20 == m1.m20 && m21 == m1.m21 && m22 == m1.m22
        } catch (var3: NullPointerException) {
            false
        }
    }

    override fun equals(t1: Any?): Boolean {
        return try {
            val m2 = t1 as Matrix3d?
            m00 == m2!!.m00 && m01 == m2.m01 && m02 == m2.m02 && m10 == m2.m10 && m11 == m2.m11 && m12 == m2.m12 && m20 == m2.m20 && m21 == m2.m21 && m22 == m2.m22
        } catch (var3: ClassCastException) {
            false
        } catch (var4: NullPointerException) {
            false
        }
    }

    fun epsilonEquals(m1: Matrix3d, epsilon: Double): Boolean {
        var diff = m00 - m1.m00
        return if ((if (diff < 0.0) -diff else diff) > epsilon) {
            false
        } else {
            diff = m01 - m1.m01
            if ((if (diff < 0.0) -diff else diff) > epsilon) {
                false
            } else {
                diff = m02 - m1.m02
                if ((if (diff < 0.0) -diff else diff) > epsilon) {
                    false
                } else {
                    diff = m10 - m1.m10
                    if ((if (diff < 0.0) -diff else diff) > epsilon) {
                        false
                    } else {
                        diff = m11 - m1.m11
                        if ((if (diff < 0.0) -diff else diff) > epsilon) {
                            false
                        } else {
                            diff = m12 - m1.m12
                            if ((if (diff < 0.0) -diff else diff) > epsilon) {
                                false
                            } else {
                                diff = m20 - m1.m20
                                if ((if (diff < 0.0) -diff else diff) > epsilon) {
                                    false
                                } else {
                                    diff = m21 - m1.m21
                                    if ((if (diff < 0.0) -diff else diff) > epsilon) {
                                        false
                                    } else {
                                        diff = m22 - m1.m22
                                        (if (diff < 0.0) -diff else diff) <= epsilon
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    fun setZero() {
        m00 = 0.0
        m01 = 0.0
        m02 = 0.0
        m10 = 0.0
        m11 = 0.0
        m12 = 0.0
        m20 = 0.0
        m21 = 0.0
        m22 = 0.0
    }

    fun negate() {
        m00 = -m00
        m01 = -m01
        m02 = -m02
        m10 = -m10
        m11 = -m11
        m12 = -m12
        m20 = -m20
        m21 = -m21
        m22 = -m22
    }

    fun negate(m1: Matrix3d) {
        m00 = -m1.m00
        m01 = -m1.m01
        m02 = -m1.m02
        m10 = -m1.m10
        m11 = -m1.m11
        m12 = -m1.m12
        m20 = -m1.m20
        m21 = -m1.m21
        m22 = -m1.m22
    }

    fun transform(t: Tuple3d) {
        val x = m00 * t.x + m01 * t.y + m02 * t.z
        val y = m10 * t.x + m11 * t.y + m12 * t.z
        val z = m20 * t.x + m21 * t.y + m22 * t.z
        t[x, y] = z
    }

    fun transform(t: Tuple3d, result: Tuple3d) {
        val x = m00 * t.x + m01 * t.y + m02 * t.z
        val y = m10 * t.x + m11 * t.y + m12 * t.z
        result.z = m20 * t.x + m21 * t.y + m22 * t.z
        result.x = x
        result.y = y
    }

    fun getScaleRotate(scales: DoubleArray, rots: DoubleArray) {
        val tmp = doubleArrayOf(
            m00,
            m01,
            m02,
            m10,
            m11,
            m12,
            m20,
            m21,
            m22
        )
        compute_svd(tmp, scales, rots)
    }

    public fun clone(): Matrix3d {
        var clone: Matrix3d = Matrix3d(m00,m01,m02,m10,m11,m12,m20,m21,m22)
        return clone
    }

    companion object {
        private const val EPS = 1.110223024E-16
        private const val ERR_EPS = 1.0E-8
        private const val xin = 0.0
        private const val yin = 0.0
        private const val zin = 0.0
        private const val xout = 0.0
        private const val yout = 0.0
        private const val zout = 0.0
        fun luDecomposition(
            matrix0: DoubleArray,
            row_perm: IntArray
        ): Boolean {
            val row_scale = DoubleArray(3)
            var i = 0
            var imax = 0
            var j: Int
            var big: Double
            j = 3
            while (j-- != 0) {
                big = 0.0
                var var4 = 3
                while (var4-- != 0) {
                    var temp = matrix0[i++]
                    temp = abs(temp)
                    if (temp > big) {
                        big = temp
                    }
                }
                if (big == 0.0) {
                    return false
                }
                row_scale[imax++] = 1.0 / big
            }
            val mtx = 0
            j = 0
            while (j < 3) {
                var target: Int
                var p2: Int
                var sum: Double
                var k: Int
                var p1: Int
                i = 0
                while (i < j) {
                    target = mtx + 3 * i + j
                    sum = matrix0[target]
                    k = i
                    p1 = mtx + 3 * i
                    p2 = mtx + j
                    while (k-- != 0) {
                        sum -= matrix0[p1] * matrix0[p2]
                        ++p1
                        p2 += 3
                    }
                    matrix0[target] = sum
                    ++i
                }
                big = 0.0 // originally double big = 0.0D;
                imax = -1
                var temp: Double
                i = j
                while (i < 3) {
                    target = mtx + 3 * i + j
                    sum = matrix0[target]
                    k = j
                    p1 = mtx + 3 * i
                    p2 = mtx + j
                    while (k-- != 0) {
                        sum -= matrix0[p1] * matrix0[p2]
                        ++p1
                        p2 += 3
                    }
                    matrix0[target] = sum
                    if (row_scale[i] * abs(sum).also { temp = it } >= big) {
                        big = temp
                        imax = i
                    }
                    ++i
                }
                if (imax < 0) {
                    throw RuntimeException()
                }
                if (j != imax) {
                    k = 3
                    p1 = mtx + 3 * imax
                    p2 = mtx + 3 * j
                    while (k-- != 0) {
                        temp = matrix0[p1]
                        matrix0[p1++] = matrix0[p2]
                        matrix0[p2++] = temp
                    }
                    row_scale[imax] = row_scale[j]
                }
                row_perm[j] = imax
                if (matrix0[mtx + 3 * j + j] == 0.0) {
                    return false
                }
                if (j != 2) {
                    temp = 1.0 / matrix0[mtx + 3 * j + j]
                    target = mtx + 3 * (j + 1) + j
                    i = 2 - j
                    while (i-- != 0) {
                        matrix0[target] *= temp
                        target += 3
                    }
                }
                ++j
            }
            return true
        }

        fun luBacksubstitution(
            matrix1: DoubleArray,
            row_perm: IntArray,
            matrix2: DoubleArray
        ) {
            val rp = 0
            for (k in 0..2) {
                var ii = -1
                var rv: Int
                for (i in 0..2) {
                    val ip = row_perm[rp + i]
                    var sum = matrix2[k + 3 * ip]
                    matrix2[k + 3 * ip] = matrix2[k + 3 * i]
                    if (ii >= 0) {
                        rv = i * 3
                        for (j in ii..i - 1) {
                            sum -= matrix1[rv + j] * matrix2[k + 3 * j]
                        }
                    } else if (sum != 0.0) {
                        ii = i
                    }
                    matrix2[k + 3 * i] = sum
                }
                rv = 6 // originally int rv = 6;
                matrix2[k + 6] /= matrix1[rv + 2]
                rv = rv - 3
                matrix2[k + 3] =
                    (matrix2[k + 3] - matrix1[rv + 2] * matrix2[k + 6]) / matrix1[rv + 1]
                rv -= 3
                matrix2[k + 0] =
                    (matrix2[k + 0] - matrix1[rv + 1] * matrix2[k + 3] - matrix1[rv + 2] * matrix2[k + 6]) / matrix1[rv + 0]
            }
        }

        fun compute_svd(
            m: DoubleArray,
            outScale: DoubleArray,
            outRot: DoubleArray
        ) {
            val u1 = DoubleArray(9)
            val v1 = DoubleArray(9)
            val t1 = DoubleArray(9)
            val t2 = DoubleArray(9)
            val rot = DoubleArray(9)
            val e = DoubleArray(3)
            val scales = DoubleArray(3)
            var negCnt = 0
            var i: Int
            i = 0
            while (i < 9) {
                rot[i] = m[i]
                ++i
            }
            var g: Double
            if (m[3] * m[3] < 1.110223024E-16) {
                u1[0] = 1.0
                u1[1] = 0.0
                u1[2] = 0.0
                u1[3] = 0.0
                u1[4] = 1.0
                u1[5] = 0.0
                u1[6] = 0.0
                u1[7] = 0.0
                u1[8] = 1.0
            } else if (m[0] * m[0] < 1.110223024E-16) {
                t1[0] = m[0]
                t1[1] = m[1]
                t1[2] = m[2]
                m[0] = m[3]
                m[1] = m[4]
                m[2] = m[5]
                m[3] = -t1[0]
                m[4] = -t1[1]
                m[5] = -t1[2]
                u1[0] = 0.0
                u1[1] = 1.0
                u1[2] = 0.0
                u1[3] = -1.0
                u1[4] = 0.0
                u1[5] = 0.0
                u1[6] = 0.0
                u1[7] = 0.0
                u1[8] = 1.0
            } else {
                g = 1.0 / sqrt(m[0] * m[0] + m[3] * m[3])
                val c1 = m[0] * g
                val s1 = m[3] * g
                t1[0] = c1 * m[0] + s1 * m[3]
                t1[1] = c1 * m[1] + s1 * m[4]
                t1[2] = c1 * m[2] + s1 * m[5]
                m[3] = -s1 * m[0] + c1 * m[3]
                m[4] = -s1 * m[1] + c1 * m[4]
                m[5] = -s1 * m[2] + c1 * m[5]
                m[0] = t1[0]
                m[1] = t1[1]
                m[2] = t1[2]
                u1[0] = c1
                u1[1] = s1
                u1[2] = 0.0
                u1[3] = -s1
                u1[4] = c1
                u1[5] = 0.0
                u1[6] = 0.0
                u1[7] = 0.0
                u1[8] = 1.0
            }
            if (m[6] * m[6] >= 1.110223024E-16) {
                if (m[0] * m[0] < 1.110223024E-16) {
                    t1[0] = m[0]
                    t1[1] = m[1]
                    t1[2] = m[2]
                    m[0] = m[6]
                    m[1] = m[7]
                    m[2] = m[8]
                    m[6] = -t1[0]
                    m[7] = -t1[1]
                    m[8] = -t1[2]
                    t1[0] = u1[0]
                    t1[1] = u1[1]
                    t1[2] = u1[2]
                    u1[0] = u1[6]
                    u1[1] = u1[7]
                    u1[2] = u1[8]
                    u1[6] = -t1[0]
                    u1[7] = -t1[1]
                    u1[8] = -t1[2]
                } else {
                    g = 1.0 / sqrt(m[0] * m[0] + m[6] * m[6])
                    val c2 = m[0] * g
                    val s2 = m[6] * g
                    t1[0] = c2 * m[0] + s2 * m[6]
                    t1[1] = c2 * m[1] + s2 * m[7]
                    t1[2] = c2 * m[2] + s2 * m[8]
                    m[6] = -s2 * m[0] + c2 * m[6]
                    m[7] = -s2 * m[1] + c2 * m[7]
                    m[8] = -s2 * m[2] + c2 * m[8]
                    m[0] = t1[0]
                    m[1] = t1[1]
                    m[2] = t1[2]
                    t1[0] = c2 * u1[0]
                    t1[1] = c2 * u1[1]
                    u1[2] = s2
                    t1[6] = -u1[0] * s2
                    t1[7] = -u1[1] * s2
                    u1[8] = c2
                    u1[0] = t1[0]
                    u1[1] = t1[1]
                    u1[6] = t1[6]
                    u1[7] = t1[7]
                }
            }
            if (m[2] * m[2] < 1.110223024E-16) {
                v1[0] = 1.0
                v1[1] = 0.0
                v1[2] = 0.0
                v1[3] = 0.0
                v1[4] = 1.0
                v1[5] = 0.0
                v1[6] = 0.0
                v1[7] = 0.0
                v1[8] = 1.0
            } else if (m[1] * m[1] < 1.110223024E-16) {
                t1[2] = m[2]
                t1[5] = m[5]
                t1[8] = m[8]
                m[2] = -m[1]
                m[5] = -m[4]
                m[8] = -m[7]
                m[1] = t1[2]
                m[4] = t1[5]
                m[7] = t1[8]
                v1[0] = 1.0
                v1[1] = 0.0
                v1[2] = 0.0
                v1[3] = 0.0
                v1[4] = 0.0
                v1[5] = -1.0
                v1[6] = 0.0
                v1[7] = 1.0
                v1[8] = 0.0
            } else {
                g = 1.0 / sqrt(m[1] * m[1] + m[2] * m[2])
                val c3 = m[1] * g
                val s3 = m[2] * g
                t1[1] = c3 * m[1] + s3 * m[2]
                m[2] = -s3 * m[1] + c3 * m[2]
                m[1] = t1[1]
                t1[4] = c3 * m[4] + s3 * m[5]
                m[5] = -s3 * m[4] + c3 * m[5]
                m[4] = t1[4]
                t1[7] = c3 * m[7] + s3 * m[8]
                m[8] = -s3 * m[7] + c3 * m[8]
                m[7] = t1[7]
                v1[0] = 1.0
                v1[1] = 0.0
                v1[2] = 0.0
                v1[3] = 0.0
                v1[4] = c3
                v1[5] = -s3
                v1[6] = 0.0
                v1[7] = s3
                v1[8] = c3
            }
            if (m[7] * m[7] >= 1.110223024E-16) {
                if (m[4] * m[4] < 1.110223024E-16) {
                    t1[3] = m[3]
                    t1[4] = m[4]
                    t1[5] = m[5]
                    m[3] = m[6]
                    m[4] = m[7]
                    m[5] = m[8]
                    m[6] = -t1[3]
                    m[7] = -t1[4]
                    m[8] = -t1[5]
                    t1[3] = u1[3]
                    t1[4] = u1[4]
                    t1[5] = u1[5]
                    u1[3] = u1[6]
                    u1[4] = u1[7]
                    u1[5] = u1[8]
                    u1[6] = -t1[3]
                    u1[7] = -t1[4]
                    u1[8] = -t1[5]
                } else {
                    g = 1.0 / sqrt(m[4] * m[4] + m[7] * m[7])
                    val c4 = m[4] * g
                    val s4 = m[7] * g
                    t1[3] = c4 * m[3] + s4 * m[6]
                    m[6] = -s4 * m[3] + c4 * m[6]
                    m[3] = t1[3]
                    t1[4] = c4 * m[4] + s4 * m[7]
                    m[7] = -s4 * m[4] + c4 * m[7]
                    m[4] = t1[4]
                    t1[5] = c4 * m[5] + s4 * m[8]
                    m[8] = -s4 * m[5] + c4 * m[8]
                    m[5] = t1[5]
                    t1[3] = c4 * u1[3] + s4 * u1[6]
                    u1[6] = -s4 * u1[3] + c4 * u1[6]
                    u1[3] = t1[3]
                    t1[4] = c4 * u1[4] + s4 * u1[7]
                    u1[7] = -s4 * u1[4] + c4 * u1[7]
                    u1[4] = t1[4]
                    t1[5] = c4 * u1[5] + s4 * u1[8]
                    u1[8] = -s4 * u1[5] + c4 * u1[8]
                    u1[5] = t1[5]
                }
            }
            t2[0] = m[0]
            t2[1] = m[4]
            t2[2] = m[8]
            e[0] = m[1]
            e[1] = m[5]
            if (e[0] * e[0] >= 1.110223024E-16 || e[1] * e[1] >= 1.110223024E-16) {
                compute_qr(t2, e, u1, v1)
            }
            scales[0] = t2[0]
            scales[1] = t2[1]
            scales[2] = t2[2]
            if (almostEqual(
                    abs(scales[0]),
                    1.0
                ) && almostEqual(
                    abs(scales[1]),
                    1.0
                ) && almostEqual(abs(scales[2]), 1.0)
            ) {
                i = 0
                while (i < 3) {
                    if (scales[i] < 0.0) {
                        ++negCnt
                    }
                    ++i
                }
                if (negCnt == 0 || negCnt == 2) {
                    outScale[2] = 1.0
                    outScale[1] = outScale[2]
                    outScale[0] = outScale[1]
                    i = 0
                    while (i < 9) {
                        outRot[i] = rot[i]
                        ++i
                    }
                    return
                }
            }
            transpose_mat(u1, t1)
            transpose_mat(v1, t2)
            svdReorder(m, t1, t2, scales, outRot, outScale)
        }

        fun svdReorder(
            m: DoubleArray,
            t1: DoubleArray,
            t2: DoubleArray,
            scales: DoubleArray,
            outRot: DoubleArray,
            outScale: DoubleArray
        ) {
            val out = IntArray(3)
            val `in` = IntArray(3)
            val mag = DoubleArray(3)
            val rot = DoubleArray(9)
            if (scales[0] < 0.0) {
                scales[0] = -scales[0]
                t2[0] = -t2[0]
                t2[1] = -t2[1]
                t2[2] = -t2[2]
            }
            if (scales[1] < 0.0) {
                scales[1] = -scales[1]
                t2[3] = -t2[3]
                t2[4] = -t2[4]
                t2[5] = -t2[5]
            }
            if (scales[2] < 0.0) {
                scales[2] = -scales[2]
                t2[6] = -t2[6]
                t2[7] = -t2[7]
                t2[8] = -t2[8]
            }
            mat_mul(t1, t2, rot)
            if (almostEqual(
                    abs(scales[0]),
                    abs(scales[1])
                ) && almostEqual(
                    abs(scales[1]),
                    abs(scales[2])
                )
            ) {
                var i: Int
                i = 0
                while (i < 9) {
                    outRot[i] = rot[i]
                    ++i
                }
                i = 0
                while (i < 3) {
                    outScale[i] = scales[i]
                    ++i
                }
            } else {
                if (scales[0] > scales[1]) {
                    if (scales[0] > scales[2]) {
                        if (scales[2] > scales[1]) {
                            out[0] = 0
                            out[1] = 2
                            out[2] = 1
                        } else {
                            out[0] = 0
                            out[1] = 1
                            out[2] = 2
                        }
                    } else {
                        out[0] = 2
                        out[1] = 0
                        out[2] = 1
                    }
                } else if (scales[1] > scales[2]) {
                    if (scales[2] > scales[0]) {
                        out[0] = 1
                        out[1] = 2
                        out[2] = 0
                    } else {
                        out[0] = 1
                        out[1] = 0
                        out[2] = 2
                    }
                } else {
                    out[0] = 2
                    out[1] = 1
                    out[2] = 0
                }
                mag[0] = m[0] * m[0] + m[1] * m[1] + m[2] * m[2]
                mag[1] = m[3] * m[3] + m[4] * m[4] + m[5] * m[5]
                mag[2] = m[6] * m[6] + m[7] * m[7] + m[8] * m[8]
                val in0: Byte
                val in1: Byte
                val in2: Byte
                if (mag[0] > mag[1]) {
                    if (mag[0] > mag[2]) {
                        if (mag[2] > mag[1]) {
                            in0 = 0
                            in2 = 1
                            in1 = 2
                        } else {
                            in0 = 0
                            in1 = 1
                            in2 = 2
                        }
                    } else {
                        in2 = 0
                        in0 = 1
                        in1 = 2
                    }
                } else if (mag[1] > mag[2]) {
                    if (mag[2] > mag[0]) {
                        in1 = 0
                        in2 = 1
                        in0 = 2
                    } else {
                        in1 = 0
                        in0 = 1
                        in2 = 2
                    }
                } else {
                    in2 = 0
                    in1 = 1
                    in0 = 2
                }
                var index = out[in0.toInt()]
                outScale[0] = scales[index]
                index = out[in1.toInt()]
                outScale[1] = scales[index]
                index = out[in2.toInt()]
                outScale[2] = scales[index]
                index = out[in0.toInt()]
                outRot[0] = rot[index]
                index = out[in0.toInt()] + 3
                outRot[3] = rot[index]
                index = out[in0.toInt()] + 6
                outRot[6] = rot[index]
                index = out[in1.toInt()]
                outRot[1] = rot[index]
                index = out[in1.toInt()] + 3
                outRot[4] = rot[index]
                index = out[in1.toInt()] + 6
                outRot[7] = rot[index]
                index = out[in2.toInt()]
                outRot[2] = rot[index]
                index = out[in2.toInt()] + 3
                outRot[5] = rot[index]
                index = out[in2.toInt()] + 6
                outRot[8] = rot[index]
            }
        }

        fun compute_qr(
            s: DoubleArray,
            e: DoubleArray,
            u: DoubleArray,
            v: DoubleArray
        ): Int {
            val cosl = DoubleArray(2)
            val cosr = DoubleArray(2)
            val sinl = DoubleArray(2)
            val sinr = DoubleArray(2)
            val m = DoubleArray(9)
            val MAX_INTERATIONS = 1 // Originally true
            val CONVERGE_TOL = 4.89E-15
            val c_b48 = 1.0
            val c_b71 = -1.0
            var converged = false
            var first = 1
            if (abs(e[1]) < 4.89E-15 || abs(e[0]) < 4.89E-15) {
                converged = true
            }
            var utemp: Double
            var vtemp: Double
            var k = 0
            while (k < 10 && !converged) {
                val shift =
                    compute_shift(s[1], e[1], s[2])
                var f =
                    (abs(s[0]) - shift) * (d_sign(
                        c_b48,
                        s[0]
                    ) + shift / s[0])
                var g = e[0]
                compute_rot(f, g, sinr, cosr, 0, first)
                f = cosr[0] * s[0] + sinr[0] * e[0]
                e[0] = cosr[0] * e[0] - sinr[0] * s[0]
                g = sinr[0] * s[1]
                s[1] = cosr[0] * s[1]
                var r = compute_rot(f, g, sinl, cosl, 0, first)
                first = 0
                s[0] = r
                f = cosl[0] * e[0] + sinl[0] * s[1]
                s[1] = cosl[0] * s[1] - sinl[0] * e[0]
                g = sinl[0] * e[1]
                e[1] = cosl[0] * e[1]
                r = compute_rot(f, g, sinr, cosr, 1, first)
                e[0] = r
                f = cosr[1] * s[1] + sinr[1] * e[1]
                e[1] = cosr[1] * e[1] - sinr[1] * s[1]
                g = sinr[1] * s[2]
                s[2] = cosr[1] * s[2]
                r = compute_rot(f, g, sinl, cosl, 1, first)
                s[1] = r
                f = cosl[1] * e[1] + sinl[1] * s[2]
                s[2] = cosl[1] * s[2] - sinl[1] * e[1]
                e[1] = f
                utemp = u[0]
                u[0] = cosl[0] * utemp + sinl[0] * u[3]
                u[3] = -sinl[0] * utemp + cosl[0] * u[3]
                utemp = u[1]
                u[1] = cosl[0] * utemp + sinl[0] * u[4]
                u[4] = -sinl[0] * utemp + cosl[0] * u[4]
                utemp = u[2]
                u[2] = cosl[0] * utemp + sinl[0] * u[5]
                u[5] = -sinl[0] * utemp + cosl[0] * u[5]
                utemp = u[3]
                u[3] = cosl[1] * utemp + sinl[1] * u[6]
                u[6] = -sinl[1] * utemp + cosl[1] * u[6]
                utemp = u[4]
                u[4] = cosl[1] * utemp + sinl[1] * u[7]
                u[7] = -sinl[1] * utemp + cosl[1] * u[7]
                utemp = u[5]
                u[5] = cosl[1] * utemp + sinl[1] * u[8]
                u[8] = -sinl[1] * utemp + cosl[1] * u[8]
                vtemp = v[0]
                v[0] = cosr[0] * vtemp + sinr[0] * v[1]
                v[1] = -sinr[0] * vtemp + cosr[0] * v[1]
                vtemp = v[3]
                v[3] = cosr[0] * vtemp + sinr[0] * v[4]
                v[4] = -sinr[0] * vtemp + cosr[0] * v[4]
                vtemp = v[6]
                v[6] = cosr[0] * vtemp + sinr[0] * v[7]
                v[7] = -sinr[0] * vtemp + cosr[0] * v[7]
                vtemp = v[1]
                v[1] = cosr[1] * vtemp + sinr[1] * v[2]
                v[2] = -sinr[1] * vtemp + cosr[1] * v[2]
                vtemp = v[4]
                v[4] = cosr[1] * vtemp + sinr[1] * v[5]
                v[5] = -sinr[1] * vtemp + cosr[1] * v[5]
                vtemp = v[7]
                v[7] = cosr[1] * vtemp + sinr[1] * v[8]
                v[8] = -sinr[1] * vtemp + cosr[1] * v[8]
                m[0] = s[0]
                m[1] = e[0]
                m[2] = 0.0
                m[3] = 0.0
                m[4] = s[1]
                m[5] = e[1]
                m[6] = 0.0
                m[7] = 0.0
                m[8] = s[2]
                if (abs(e[1]) < 4.89E-15 || abs(e[0]) < 4.89E-15) {
                    converged = true
                }
                ++k
            }
            if (abs(e[1]) < 4.89E-15) {
                compute_2X2(
                    s[0],
                    e[0],
                    s[1],
                    s,
                    sinl,
                    cosl,
                    sinr,
                    cosr,
                    0
                )
                utemp = u[0]
                u[0] = cosl[0] * utemp + sinl[0] * u[3]
                u[3] = -sinl[0] * utemp + cosl[0] * u[3]
                utemp = u[1]
                u[1] = cosl[0] * utemp + sinl[0] * u[4]
                u[4] = -sinl[0] * utemp + cosl[0] * u[4]
                utemp = u[2]
                u[2] = cosl[0] * utemp + sinl[0] * u[5]
                u[5] = -sinl[0] * utemp + cosl[0] * u[5]
                vtemp = v[0]
                v[0] = cosr[0] * vtemp + sinr[0] * v[1]
                v[1] = -sinr[0] * vtemp + cosr[0] * v[1]
                vtemp = v[3]
                v[3] = cosr[0] * vtemp + sinr[0] * v[4]
                v[4] = -sinr[0] * vtemp + cosr[0] * v[4]
                vtemp = v[6]
                v[6] = cosr[0] * vtemp + sinr[0] * v[7]
                v[7] = -sinr[0] * vtemp + cosr[0] * v[7]
            } else {
                compute_2X2(
                    s[1],
                    e[1],
                    s[2],
                    s,
                    sinl,
                    cosl,
                    sinr,
                    cosr,
                    1
                )
                utemp = u[3]
                u[3] = cosl[0] * utemp + sinl[0] * u[6]
                u[6] = -sinl[0] * utemp + cosl[0] * u[6]
                utemp = u[4]
                u[4] = cosl[0] * utemp + sinl[0] * u[7]
                u[7] = -sinl[0] * utemp + cosl[0] * u[7]
                utemp = u[5]
                u[5] = cosl[0] * utemp + sinl[0] * u[8]
                u[8] = -sinl[0] * utemp + cosl[0] * u[8]
                vtemp = v[1]
                v[1] = cosr[0] * vtemp + sinr[0] * v[2]
                v[2] = -sinr[0] * vtemp + cosr[0] * v[2]
                vtemp = v[4]
                v[4] = cosr[0] * vtemp + sinr[0] * v[5]
                v[5] = -sinr[0] * vtemp + cosr[0] * v[5]
                vtemp = v[7]
                v[7] = cosr[0] * vtemp + sinr[0] * v[8]
                v[8] = -sinr[0] * vtemp + cosr[0] * v[8]
            }
            return 0
        }

        fun max(a: Double, b: Double): Double {
            return if (a > b) a else b
        }

        fun min(a: Double, b: Double): Double {
            return if (a < b) a else b
        }

        fun d_sign(a: Double, b: Double): Double {
            val x = if (a >= 0.0) a else -a
            return if (b >= 0.0) x else -x
        }

        fun compute_shift(f: Double, g: Double, h: Double): Double {
            val fa = abs(f)
            val ga = abs(g)
            val ha = abs(h)
            val fhmn = min(fa, ha)
            val fhmx = max(fa, ha)
            val d__1: Double
            var ssmin: Double
            if (fhmn == 0.0) {
                ssmin = 0.0
                if (fhmx != 0.0) {
                    d__1 = min(fhmx, ga) / max(fhmx, ga)
                }
            } else {
                val c: Double
                val `as`: Double
                val at: Double
                val au: Double
                if (ga < fhmx) {
                    `as` = fhmn / fhmx + 1.0
                    at = (fhmx - fhmn) / fhmx
                    d__1 = ga / fhmx
                    au = d__1 * d__1
                    c =
                        2.0 / (sqrt(`as` * `as` + au) + sqrt(at * at + au))
                    ssmin = fhmn * c
                } else {
                    au = fhmx / ga
                    if (au == 0.0) {
                        ssmin = fhmn * fhmx / ga
                    } else {
                        `as` = fhmn / fhmx + 1.0
                        at = (fhmx - fhmn) / fhmx
                        d__1 = `as` * au
                        val d__2 = at * au
                        c =
                            1.0 / (sqrt(d__1 * d__1 + 1.0) + sqrt(d__2 * d__2 + 1.0))
                        ssmin = fhmn * c * au
                        ssmin += ssmin
                    }
                }
            }
            return ssmin
        }

        fun compute_2X2(
            f: Double,
            g: Double,
            h: Double,
            single_values: DoubleArray,
            snl: DoubleArray,
            csl: DoubleArray,
            snr: DoubleArray,
            csr: DoubleArray,
            index: Int
        ): Int {
            val c_b3 = 2.0
            val c_b4 = 1.0
            var ssmax = single_values[0]
            var ssmin = single_values[1]
            var clt = 0.0
            var crt = 0.0
            var slt = 0.0
            var srt = 0.0
            var tsign = 0.0
            var ft = f
            var fa = abs(f)
            var ht = h
            var ha = abs(h)
            var pmax = 1
            val swap: Boolean
            swap = if (ha > fa) {
                true
            } else {
                false
            }
            if (swap) {
                pmax = 3
                ft = h
                ht = f
                val temp = fa
                fa = ha
                ha = temp
            }
            val ga = abs(g)
            if (ga == 0.0) {
                single_values[1] = ha
                single_values[0] = fa
                clt = 1.0
                crt = 1.0
                slt = 0.0
                srt = 0.0
            } else {
                var gasmal = true
                if (ga > fa) {
                    pmax = 2
                    if (fa / ga < 1.110223024E-16) {
                        gasmal = false
                        ssmax = ga
                        ssmin = if (ha > 1.0) {
                            fa / (ga / ha)
                        } else {
                            fa / ga * ha
                        }
                        clt = 1.0
                        slt = ht / g
                        srt = 1.0
                        crt = ft / g
                    }
                }
                if (gasmal) {
                    var d = fa - ha
                    var l: Double
                    l = if (d == fa) {
                        1.0
                    } else {
                        d / fa
                    }
                    var m = g / ft
                    var t = 2.0 - l
                    var mm = m * m
                    var tt = t * t
                    var s = sqrt(tt + mm)
                    var r: Double
                    r = if (l == 0.0) {
                        abs(m)
                    } else {
                        sqrt(l * l + mm)
                    }
                    var a = (s + r) * 0.5
                    if (ga > fa) {
                        pmax = 2
                        if (fa / ga < 1.110223024E-16) {
                            gasmal = false
                            ssmax = ga
                            ssmin = if (ha > 1.0) {
                                fa / (ga / ha)
                            } else {
                                fa / ga * ha
                            }
                            clt = 1.0
                            slt = ht / g
                            srt = 1.0
                            crt = ft / g
                        }
                    }
                    if (gasmal) {
                        d = fa - ha
                        l = if (d == fa) {
                            1.0
                        } else {
                            d / fa
                        }
                        m = g / ft
                        t = 2.0 - l
                        mm = m * m
                        tt = t * t
                        s = sqrt(tt + mm)
                        r = if (l == 0.0) {
                            abs(m)
                        } else {
                            sqrt(l * l + mm)
                        }
                        a = (s + r) * 0.5
                        ssmin = ha / a
                        ssmax = fa * a
                        t = if (mm == 0.0) {
                            if (l == 0.0) {
                                d_sign(c_b3, ft) * d_sign(
                                    c_b4,
                                    g
                                )
                            } else {
                                g / d_sign(d, ft) + m / t
                            }
                        } else {
                            (m / (s + t) + m / (r + l)) * (a + 1.0)
                        }
                        l = sqrt(t * t + 4.0)
                        crt = 2.0 / l
                        srt = t / l
                        clt = (crt + srt * m) / a
                        slt = ht / ft * srt / a
                    }
                }
                if (swap) {
                    csl[0] = srt
                    snl[0] = crt
                    csr[0] = slt
                    snr[0] = clt
                } else {
                    csl[0] = clt
                    snl[0] = slt
                    csr[0] = crt
                    snr[0] = srt
                }
                if (pmax == 1) {
                    tsign = d_sign(
                        c_b4,
                        csr[0]
                    ) * d_sign(
                        c_b4,
                        csl[0]
                    ) * d_sign(c_b4, f)
                }
                if (pmax == 2) {
                    tsign = d_sign(
                        c_b4,
                        snr[0]
                    ) * d_sign(
                        c_b4,
                        csl[0]
                    ) * d_sign(c_b4, g)
                }
                if (pmax == 3) {
                    tsign = d_sign(
                        c_b4,
                        snr[0]
                    ) * d_sign(
                        c_b4,
                        snl[0]
                    ) * d_sign(c_b4, h)
                }
                single_values[index] = d_sign(ssmax, tsign)
                val d__1 =
                    tsign * d_sign(c_b4, f) * d_sign(c_b4, h)
                single_values[index + 1] = d_sign(ssmin, d__1)
            }
            return 0
        }

        fun compute_rot(
            f: Double,
            g: Double,
            sin: DoubleArray,
            cos: DoubleArray,
            index: Int,
            first: Int
        ): Double {
            val safmn2 = 2.002083095183101E-146
            val safmx2 = 4.9947976805055876E145
            var cs: Double
            var sn: Double
            var r: Double
            if (g == 0.0) {
                cs = 1.0
                sn = 0.0
                r = f
            } else if (f == 0.0) {
                cs = 0.0
                sn = 1.0
                r = g
            } else {
                var f1 = f
                var g1 = g
                var scale =
                    max(abs(f), abs(g))
                var i: Int
                var count: Int
                if (scale >= 4.9947976805055876E145) {
                    count = 0
                    while (scale >= 4.9947976805055876E145) {
                        ++count
                        f1 *= 2.002083095183101E-146
                        g1 *= 2.002083095183101E-146
                        scale =
                            max(abs(f1), abs(g1))
                    }
                    r = sqrt(f1 * f1 + g1 * g1)
                    cs = f1 / r
                    sn = g1 / r
                    i = 1
                    while (i <= count) {
                        r *= 4.9947976805055876E145
                        ++i
                    }
                } else if (scale > 2.002083095183101E-146) {
                    r = sqrt(f * f + g * g)
                    cs = f / r
                    sn = g / r
                } else {
                    count = 0
                    while (scale <= 2.002083095183101E-146) {
                        ++count
                        f1 *= 4.9947976805055876E145
                        g1 *= 4.9947976805055876E145
                        scale =
                            max(abs(f1), abs(g1))
                    }
                    r = sqrt(f1 * f1 + g1 * g1)
                    cs = f1 / r
                    sn = g1 / r
                    i = 1
                    while (i <= count) {
                        r *= 2.002083095183101E-146
                        ++i
                    }
                }
                if (abs(f) > abs(g) && cs < 0.0) {
                    cs = -cs
                    sn = -sn
                    r = -r
                }
            }
            sin[index] = sn
            cos[index] = cs
            return r
        }

        fun print_mat(mat: DoubleArray) {
            for (i in 0..2) {
                println(
                    mat[i * 3 + 0]
                        .toString() + " " + mat[i * 3 + 1] + " " + mat[i * 3 + 2] + "\n"
                )
            }
        }

        fun print_det(mat: DoubleArray) {
            val det =
                mat[0] * mat[4] * mat[8] + mat[1] * mat[5] * mat[6] + mat[2] * mat[3] * mat[7] - mat[2] * mat[4] * mat[6] - mat[0] * mat[5] * mat[7] - mat[1] * mat[3] * mat[8]
            println("det= $det")
        }

        fun mat_mul(
            m1: DoubleArray,
            m2: DoubleArray,
            m3: DoubleArray
        ) {
            val tmp = doubleArrayOf(
                m1[0] * m2[0] + m1[1] * m2[3] + m1[2] * m2[6],
                m1[0] * m2[1] + m1[1] * m2[4] + m1[2] * m2[7],
                m1[0] * m2[2] + m1[1] * m2[5] + m1[2] * m2[8],
                m1[3] * m2[0] + m1[4] * m2[3] + m1[5] * m2[6],
                m1[3] * m2[1] + m1[4] * m2[4] + m1[5] * m2[7],
                m1[3] * m2[2] + m1[4] * m2[5] + m1[5] * m2[8],
                m1[6] * m2[0] + m1[7] * m2[3] + m1[8] * m2[6],
                m1[6] * m2[1] + m1[7] * m2[4] + m1[8] * m2[7],
                m1[6] * m2[2] + m1[7] * m2[5] + m1[8] * m2[8]
            )
            for (i in 0..8) {
                m3[i] = tmp[i]
            }
        }

        fun transpose_mat(`in`: DoubleArray, out: DoubleArray) {
            out[0] = `in`[0]
            out[1] = `in`[3]
            out[2] = `in`[6]
            out[3] = `in`[1]
            out[4] = `in`[4]
            out[5] = `in`[7]
            out[6] = `in`[2]
            out[7] = `in`[5]
            out[8] = `in`[8]
        }

        fun max3(values: DoubleArray): Double {
            return if (values[0] > values[1]) {
                if (values[0] > values[2]) values[0] else values[2]
            } else {
                if (values[1] > values[2]) values[1] else values[2]
            }
        }

        private fun almostEqual(a: Double, b: Double): Boolean {
            return if (a == b) {
                true
            } else {
                val EPSILON_ABSOLUTE = 1.0E-6
                val EPSILON_RELATIVE = 1.0E-4
                val diff = abs(a - b)
                val absA = abs(a)
                val absB = abs(b)
                val max = if (absA >= absB) absA else absB
                if (diff < 1.0E-6) {
                    true
                } else {
                    diff / max < 1.0E-4
                }
            }
        }
    }
}